
import React, { createContext, useState, useEffect } from 'react';
import { Debt } from '@/types/debt';
import { useProfile } from '@/contexts/profile';
import { useDebtOperations } from './debtOperations';
import { useTransactions } from '@/contexts/transactions';
import { useAuth } from '@/contexts/auth';
import { fetchDebts } from '@/lib/supabase-debts-operations';

import { DebtsContextType } from './types';

export const DebtsContext = createContext<DebtsContextType | undefined>(undefined);

export const DebtsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [debts, setDebts] = useState<Debt[]>([]);
  const { activeProfile } = useProfile();
  const { transactions } = useTransactions();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  
  // Load data from Supabase when profile changes
  useEffect(() => {
    if (!activeProfile || !user) {
      setDebts([]);
      setIsLoading(false);
      return;
    }
    
    const loadDebts = async () => {
      try {
        setIsLoading(true);
        console.log('[DebtProvider] Loading debts from Supabase');
        const loadedDebts = await fetchDebts(user.id, activeProfile.id);
        setDebts(loadedDebts);
        console.log('[DebtProvider] Loaded debts:', loadedDebts.length);
      } catch (error) {
        console.error('[DebtProvider] Error loading debts:', error);
        setDebts([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadDebts();
  }, [activeProfile?.id, user?.id]);

  const operations = useDebtOperations(debts, setDebts);

  const getTotalDebt = () => {
    return debts.reduce((total, debt) => {
      // Use debt's total amount directly
      return total + debt.totalAmount;
    }, 0);
  };

  const getTotalPaid = () => {
    return debts.reduce((total, debt) => {
      // Get debt transactions by matching description pattern
      const debtTransactions = transactions.filter(t => 
        t.description?.includes(debt.name) && 
        t.type === 'despesa' && 
        !t.deleted &&
        t.status === 'paid'
      );
      const paidAmount = debtTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);
      const entranceAmount = debt.hasEntrance && debt.entranceAmount ? debt.entranceAmount : 0;
      return total + paidAmount + entranceAmount;
    }, 0);
  };

  return (
    <DebtsContext.Provider
      value={{
        debts,
        ...operations,
        getTotalDebt,
        getTotalPaid
      }}
    >
      {children}
    </DebtsContext.Provider>
  );
};
